import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { clientsApi, reportsApi, stepsApi } from '../lib/dbApi';
import { logger } from '../lib/loggingService';

interface Client {
  id: string;
  full_name: string;
  address?: string;
}

interface Report {
  id: string;
  client_name: string;
  // A handful of legacy reports have no created_at - keep this nullable so every
  // use site is forced to guard it instead of assuming a string.
  created_at: string | null;
  pdf_uploaded: boolean;
  pdf_url: string | null;
  // The date the PDF actually landed in S3 - the folder a fresh preview/download link
  // has to be looked up under (see resolveFreshFileUrls). Can differ from created_at
  // for edited/reprocessed reports.
  pdf_uploaded_at: string | null;
}

const AVATAR_PALETTE = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-pink-100', text: 'text-pink-700' },
  { bg: 'bg-teal-100', text: 'text-teal-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
];

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
};

const getAvatarPalette = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

// Minimal inline icon set (stroke-based, currentColor) matching the requested UI.
const Icon = {
  ArrowLeft: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
  ),
  Refresh: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v6h-6" /></svg>
  ),
  Plus: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M12 5v14M5 12h14" /></svg>
  ),
  FileText: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" /></svg>
  ),
  Share: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 3.9M15.4 6.6L8.6 10.5" /></svg>
  ),
  Users: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
  ),
  Calendar: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  ),
  Search: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
  ),
  Filter: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
  ),
  Tag: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M20.59 13.41L11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59a2 2 0 0 0 .59 1.41l9.58 9.58a2 2 0 0 0 2.83 0l5.59-5.59a2 2 0 0 0 0-2.83z" /><circle cx="7.5" cy="7.5" r="1" /></svg>
  ),
  ChevronDown: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M6 9l6 6 6-6" /></svg>
  ),
  MapPin: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
  ),
  Edit: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
  ),
  Image: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
  ),
  Clock: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
  ),
  MoreVertical: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
  ),
  Eye: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
  ),
  Download: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
  ),
};

const StatCard: React.FC<{ icon: React.ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-2.5 shadow-sm">
    <div className="w-9 h-9 rounded-full bg-[#F7E6E4] text-[#722420] flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div>
      <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

const InlineSpinner: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10 text-gray-400">
    <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-[#722420] animate-spin" />
    <span className="text-xs">{label}</span>
  </div>
);

const Library: React.FC<{ userEmail: string }> = ({ userEmail }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState<'clients' | 'reports' | 'webhooks' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');

  const CLIENTS_PAGE_SIZE = 50;
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [isLoadingMoreClients, setIsLoadingMoreClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [reports, setReports] = useState<Report[]>([]);

  // Header stats
  const [totalReportsCount, setTotalReportsCount] = useState<number | null>(null);
  const [todayReportsCount, setTodayReportsCount] = useState<number | null>(null);

  // Toolbar filters (client-side, over the selected client's own reports)
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pdf' | 'no-pdf'>('all');

  const [search, setSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>('');

  // pdf_url stored on a report is a pre-signed S3 URL captured at upload time - it expires
  // (S3 "Request has expired" AccessDenied) after a matter of days, so it can never be used
  // directly once stale. Preview/Download instead re-fetch a fresh signed URL for the same
  // file on click, via the same directory-listing endpoint the old Files panel used.
  const API_BASE = process.env.REACT_APP_API_BASE || 'https://adminbackend.chimneysweeps.com';
  const API_KEY = process.env.REACT_APP_API_KEY || 'pit-0988e019-bccb-4b77-a0a6-f50e56c58522';
  const [resolvingReportId, setResolvingReportId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const slugifyClientName = (name: string) =>
    name.trim().replace(/\s+/g, '-').replace(/[^A-Za-z0-9-]/g, '');

  const selectedClient = clients.find(c => c.id === selectedClientId);

  // Helper to add debug info
  const addDebug = (msg: string) => {
    console.log(`[LIBRARY] ${msg}`);
    setDebugInfo(prev => `${prev}\n${msg}`);
  };

  // Fetch addresses for one batch of clients only (not the whole clients/reports/step1_json tables).
  const attachAddresses = async (batch: Array<{ id: string; full_name: string }>): Promise<Client[]> => {
    if (batch.length === 0) return [];
    const clientIds = batch.map(c => c.id);

    const { data: reportsData } = await reportsApi.list({ client_ids: clientIds.join(','), limit: clientIds.length * 10 });
    const reportIds = (reportsData || []).map((r: any) => r.id);

    const reportAddressMap = new Map<string, string>();
    if (reportIds.length > 0) {
      const { data: step1Data } = await stepsApi.batchGet('step1_json', reportIds);
      (step1Data || []).forEach((item: any) => {
        try {
          const addressData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
          const address = addressData['Client Address'];
          if (address) reportAddressMap.set(item.report_id, address);
        } catch (e) {
          // Silently fail on parse errors
        }
      });
    }

    const clientAddressMap = new Map<string, string>();
    (reportsData || []).forEach((report: any) => {
      if (!clientAddressMap.has(report.client_id)) {
        const address = reportAddressMap.get(report.id);
        if (address) clientAddressMap.set(report.client_id, address);
      }
    });

    return batch.map(c => ({ id: c.id, full_name: c.full_name, address: clientAddressMap.get(c.id) }));
  };

  // Load one page of clients (batched - never the whole table). `reset` starts over from
  // page 1 (used on mount and whenever the search term changes); otherwise appends the
  // next page onto what's already loaded ("Load more").
  const loadClientsPage = useCallback(async (reset: boolean, searchTerm: string) => {
    if (reset) {
      setLoading(true);
      setLoadingPhase('clients');
      setError(null);
      setDebugInfo('');
    } else {
      setIsLoadingMoreClients(true);
    }
    try {
      if (reset) {
        addDebug('Getting user');
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw userErr;
        if (!userData?.user?.id) throw new Error('Not signed in');
      }

      const offset = reset ? 0 : clients.length;
      addDebug(`Fetching clients page (offset ${offset}, search "${searchTerm}")`);
      const { data: clientsData, total } = await clientsApi.list({
        search: searchTerm || undefined,
        limit: CLIENTS_PAGE_SIZE,
        offset,
      });
      addDebug(`✓ Fetched ${clientsData?.length || 0} of ${total ?? '?'} clients`);

      const withAddresses = await attachAddresses(clientsData || []);

      setClients(prev => (reset ? withAddresses : [...prev, ...withAddresses]));
      setClientsTotal(total || 0);
    } catch (e: any) {
      const errMsg = e?.message || JSON.stringify(e);
      addDebug(`✗ Error: ${errMsg}`);
      setError(errMsg);
    } finally {
      setLoading(false);
      setIsLoadingMoreClients(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.length]);

  // Initial load, then re-search (debounced) whenever the search box changes - queries the
  // server for a fresh first page instead of filtering whatever happens to already be loaded.
  useEffect(() => {
    const delay = search ? 350 : 0;
    const handle = setTimeout(() => {
      loadClientsPage(true, search);
    }, delay);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadMoreClients = () => {
    if (isLoadingMoreClients || clients.length >= clientsTotal) return;
    loadClientsPage(false, search);
  };

  // Load reports for selected client (MANUAL REPORTS ONLY - exclude webhook-sourced)
  const loadReports = useCallback(async (clientId: string) => {
    if (!clientId) {
      setReports([]);
      return;
    }

    setLoading(true);
    setLoadingPhase('reports');
    setError(null);
    setDebugInfo('');
    try {
      addDebug(`Fetching MANUAL reports for client ${clientId}`);

      const { data: reportsData } = await reportsApi.list({ client_id: clientId, manual_only: true });

      addDebug(`✓ Fetched ${reportsData?.length || 0} manual reports`);
      setReports(reportsData || []);
      setLoadingPhase(null);
    } catch (e: any) {
      addDebug(`✗ Failed: ${e?.message}`);
      setError(e?.message);
      setReports([]);
      setLoadingPhase(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh reports when user returns to Library from editing
  useEffect(() => {
    const handleFocus = () => {
      console.log('Window focused, refreshing reports...');
      if (selectedClientId) {
        loadReports(selectedClientId);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedClientId, loadReports]);

  // When client selection changes. Also resets the toolbar filters - otherwise a filter
  // left on (e.g. "PDF Ready") from a previous client silently hides every report for a
  // client that doesn't match it, while the "N Reports" stat (unfiltered) still shows N.
  useEffect(() => {
    if (selectedClientId) {
      setDateFilter('all');
      setStatusFilter('all');
      loadReports(selectedClientId);
    }
  }, [selectedClientId, loadReports]);

  // Header stats: total reports and reports created today, across every client.
  const loadStats = useCallback(async () => {
    try {
      const [{ total: reportsTotal }, todayRes] = await Promise.all([
        reportsApi.list({ manual_only: true, limit: 1 }),
        (() => {
          const startOfToday = new Date();
          startOfToday.setHours(0, 0, 0, 0);
          return reportsApi.list({ manual_only: true, created_after: startOfToday.toISOString(), limit: 1 });
        })(),
      ]);
      setTotalReportsCount(reportsTotal ?? null);
      setTodayReportsCount(todayRes.total ?? null);
    } catch (e) {
      // Stats are supplementary - leave them blank rather than surfacing an error banner.
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handle report edit - navigates straight to the exact report clicked, rather than
  // re-deriving "whichever report happens to match this date" (see MultiStepForm.loadForEdit).
  const handleEditReport = async (report: Report) => {
    setIsEditing(true);
    try {
      await logger.logReportOpenedForEditing(userEmail, report.id, {
        client_name: selectedClient?.full_name || 'Unknown',
        date: report.created_at || '',
        source: 'library'
      });

      localStorage.setItem('edit_context', JSON.stringify({
        clientId: selectedClientId,
        reportId: report.id
      }));

      window.dispatchEvent(new CustomEvent('app:navigate', { detail: { to: '/' } }));
    } catch (e) {
      console.error('Error editing report:', e);
    } finally {
      setIsEditing(false);
    }
  };

  const reportFileName = (report: Report) => {
    if (!report.pdf_url) return null;
    // Pre-signed S3 URLs carry query params (signature, expiry, etc.) on the same
    // path segment as the filename - strip those off before taking the last segment.
    const pathOnly = report.pdf_url.split('?')[0];
    const rawName = pathOnly.split('/').pop() || '';
    try {
      return decodeURIComponent(rawName);
    } catch {
      return rawName || null;
    }
  };

  // The stored pdf_url is a pre-signed S3 URL that expires (S3 "Request has expired"
  // AccessDenied) days after it was issued, so it can't be opened directly once stale.
  // Re-fetch a fresh signature for the same file by browsing its date folder, matching
  // on filename - the folder is keyed by upload date, which for edited/reprocessed
  // reports can differ from created_at, so pdf_uploaded_at is tried first.
  const resolveFreshFileUrls = async (report: Report): Promise<{ preview_url: string; download_url: string } | null> => {
    if (!selectedClient) return null;
    const fileName = reportFileName(report);
    const dateSource = report.pdf_uploaded_at || report.created_at;
    if (!fileName || !dateSource) return null;

    const clientSlug = slugifyClientName(selectedClient.full_name);
    const prefix = `${clientSlug}/${dateSource.slice(0, 10)}/`;

    try {
      const url = `${API_BASE}/directories?prefix=${encodeURIComponent(prefix)}`;
      const res = await fetch(url, { headers: { 'X-API-Key': API_KEY } });
      if (!res.ok) return null;
      const data = await res.json();
      const files = Array.isArray(data?.files) ? data.files : [];
      const match = files.find((f: any) => (f.key.split('/').pop() || '') === fileName);
      return match ? { preview_url: match.preview_url, download_url: match.download_url } : null;
    } catch (e) {
      console.error('Failed to resolve a fresh PDF link:', e);
      return null;
    }
  };

  // Preview PDF
  const handlePreview = async (report: Report) => {
    if (!report.pdf_url) return;
    setLinkError(null);
    setResolvingReportId(report.id);
    try {
      const fresh = await resolveFreshFileUrls(report);
      if (!fresh) {
        setLinkError("This report's PDF link has expired and the original file couldn't be located. Try regenerating the report.");
        return;
      }
      setPreviewUrl(fresh.preview_url);
      setPreviewName(reportFileName(report) || 'Report.pdf');
      setShowPreview(true);
    } finally {
      setResolvingReportId(null);
    }
  };

  // Download PDF
  const handleDownload = async (report: Report) => {
    if (!report.pdf_url) return;
    setLinkError(null);
    setResolvingReportId(report.id);
    try {
      const fresh = await resolveFreshFileUrls(report);
      if (!fresh) {
        setLinkError("This report's PDF link has expired and the original file couldn't be located. Try regenerating the report.");
        return;
      }
      const win = window.open(fresh.download_url, '_blank', 'noopener');
      if (!win) {
        const a = document.createElement('a');
        a.href = fresh.download_url;
        a.download = reportFileName(report) || 'Report.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      console.error('Download error:', e);
    } finally {
      setResolvingReportId(null);
    }
  };

  const handleNewReport = () => {
    try {
      localStorage.removeItem('edit_context');
      localStorage.removeItem('webhookData');
      localStorage.removeItem('webhookDataSource');
    } catch {}
    window.dispatchEvent(new CustomEvent('app:navigate', { detail: { to: '/' } }));
  };

  // Client search is now server-side and paginated (see loadClientsPage) - `clients` already
  // reflects the current search term, so no further client-side filtering is needed here.

  // Reports for the selected client, newest first, narrowed by the toolbar's date/status
  // filters. `created_at` is nullable on a handful of legacy reports - they always pass the
  // date filter (there's no date to exclude them by) but are sorted to the end.
  const now = new Date();
  const dateFilterCutoff = (() => {
    if (dateFilter === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
    if (dateFilter === 'week') { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
    if (dateFilter === 'month') { const d = new Date(now); d.setMonth(d.getMonth() - 1); return d; }
    return null;
  })();

  const visibleReports = [...reports]
    .filter(r => (statusFilter === 'all' ? true : statusFilter === 'pdf' ? r.pdf_uploaded : !r.pdf_uploaded))
    .filter(r => (!dateFilterCutoff || !r.created_at ? true : new Date(r.created_at) >= dateFilterCutoff))
    .sort((a, b) => {
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const pdfCount = reports.filter(r => r.pdf_uploaded).length;
  const lastReportDate = reports.reduce<string | null>((latest, r) => {
    if (!r.created_at) return latest;
    if (!latest || new Date(r.created_at) > new Date(latest)) return r.created_at;
    return latest;
  }, null);

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date';

  const refresh = () => {
    if (selectedClientId) loadReports(selectedClientId);
    loadStats();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Report Library</h2>
          <p className="text-sm text-gray-500">Browse and manage your inspection reports</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 shadow-sm"
          >
            <Icon.Refresh className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={handleNewReport}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#722420] text-white text-sm font-medium rounded-xl hover:bg-[#8B2E26] shadow-sm"
          >
            <Icon.Plus className="w-4 h-4" /> New Report
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatCard icon={<Icon.Users className="w-4 h-4" />} value={clientsTotal.toLocaleString()} label="Total Clients" />
        <StatCard icon={<Icon.FileText className="w-4 h-4" />} value={totalReportsCount != null ? totalReportsCount.toLocaleString() : '—'} label="Total Reports" />
        <StatCard icon={<Icon.Calendar className="w-4 h-4" />} value={todayReportsCount != null ? todayReportsCount.toLocaleString() : '—'} label="Today's Reports" />
      </div>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Icon.Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients or reports..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#722420]/20 focus:border-[#722420]"
          />
        </div>
        <div className="relative">
          <Icon.Calendar className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as any)}
            className="appearance-none pl-10 pr-9 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#722420]/20 focus:border-[#722420]"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
          <Icon.ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="relative">
          <Icon.Filter className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="appearance-none pl-10 pr-9 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#722420]/20 focus:border-[#722420]"
          >
            <option value="all">All Status</option>
            <option value="pdf">PDF Ready</option>
            <option value="no-pdf">No PDF</option>
          </select>
          <Icon.ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="relative">
          <Icon.Tag className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select
            disabled
            defaultValue="all"
            title="No report types are tracked yet"
            className="appearance-none pl-10 pr-9 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-400 cursor-not-allowed"
          >
            <option value="all">All Types</option>
          </select>
          <Icon.ChevronDown className="w-4 h-4 text-gray-300 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 rounded-xl">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Reports View */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* Clients */}
          <div className="bg-white border border-gray-200 rounded-xl flex flex-col h-[38rem]">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">
              Clients ({clients.length} of {clientsTotal})
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && loadingPhase === 'clients' ? (
                <InlineSpinner label="Loading clients…" />
              ) : clients.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No clients</div>
              ) : (
                <div>
                  {clients.map(client => {
                    const palette = getAvatarPalette(client.full_name);
                    const isSelected = selectedClientId === client.id;
                    return (
                      <button
                        key={client.id}
                        onClick={() => setSelectedClientId(client.id)}
                        className={`w-full flex items-center gap-3 text-left px-4 py-3 border-l-2 transition-colors ${
                          isSelected ? 'bg-[#FBEEED] border-l-[#722420]' : 'border-l-transparent hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                          {getInitials(client.full_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{client.full_name}</div>
                          {client.address && (
                            <div className="text-xs text-gray-500 truncate">{client.address}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {clients.length < clientsTotal && (
                <button
                  onClick={loadMoreClients}
                  disabled={isLoadingMoreClients}
                  className="w-full p-3 text-sm font-medium text-[#722420] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed border-t border-gray-100"
                >
                  {isLoadingMoreClients ? 'Loading…' : `Load ${Math.min(CLIENTS_PAGE_SIZE, clientsTotal - clients.length)} more`}
                </button>
              )}
            </div>
          </div>

          {/* Client detail + reports */}
          <div>
            {!selectedClient ? (
              <div className="h-[38rem] flex items-center justify-center bg-white border border-dashed border-gray-300 rounded-xl text-gray-400 text-sm">
                Select a client to view their reports
              </div>
            ) : (
              <>
                {/* Client detail card */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold flex-shrink-0 ${getAvatarPalette(selectedClient.full_name).bg} ${getAvatarPalette(selectedClient.full_name).text}`}>
                      {getInitials(selectedClient.full_name)}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900">{selectedClient.full_name}</h3>
                      {selectedClient.address && (
                        <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                          <Icon.MapPin className="w-3.5 h-3.5 flex-shrink-0" /> {selectedClient.address}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Icon.FileText className="w-3.5 h-3.5" /> {reports.length} Reports</span>
                        <span className="flex items-center gap-1"><Icon.Image className="w-3.5 h-3.5" /> {pdfCount} PDFs</span>
                        <span className="flex items-center gap-1"><Icon.Clock className="w-3.5 h-3.5" /> Last report: {formatDate(lastReportDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reports list */}
                <div className="bg-white border border-gray-200 rounded-xl">
                  <div className="p-4 border-b border-gray-100 font-semibold text-gray-900">
                    Reports ({visibleReports.length})
                  </div>
                  {linkError && (
                    <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-3">
                      <p className="text-xs text-red-800">{linkError}</p>
                      <button onClick={() => setLinkError(null)} className="text-red-400 hover:text-red-600 text-xs flex-shrink-0">✕</button>
                    </div>
                  )}
                  <div className="divide-y divide-gray-100 max-h-[26rem] overflow-y-auto">
                    {loading && loadingPhase === 'reports' ? (
                      <InlineSpinner label="Loading reports…" />
                    ) : visibleReports.length === 0 ? (
                      <div className="p-8 text-center text-gray-400 text-sm">
                        {reports.length === 0 ? 'No reports for this client' : 'No reports match the current filters'}
                      </div>
                    ) : (
                      visibleReports.map((report, idx) => {
                        const fileName = reportFileName(report);
                        return (
                          <div key={report.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-14 rounded-lg bg-[#FBEEED] flex items-center justify-center flex-shrink-0">
                                <Icon.FileText className="w-5 h-5 text-[#722420]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="font-semibold text-sm text-gray-900">Report #{idx + 1}</div>
                                  {report.pdf_uploaded && (
                                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-medium rounded border border-emerald-100 flex-shrink-0">
                                      PDF
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                                  <span className="flex items-center gap-1"><Icon.Calendar className="w-3.5 h-3.5" />{formatDate(report.created_at)}</span>
                                  {report.client_name && (
                                    <span className="flex items-center gap-1"><Icon.Users className="w-3.5 h-3.5" />{report.client_name}</span>
                                  )}
                                </div>
                                {fileName && (
                                  <div className="text-xs text-gray-400 truncate mt-1">{fileName}</div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {report.pdf_url && (
                                    <button
                                      onClick={() => handlePreview(report)}
                                      disabled={resolvingReportId === report.id}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#722420] text-white text-xs font-medium rounded-lg hover:bg-[#8B2E26] disabled:opacity-50"
                                    >
                                      <Icon.Eye className="w-3.5 h-3.5" /> {resolvingReportId === report.id ? 'Opening…' : 'Preview PDF'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditReport(report)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50"
                                  >
                                    <Icon.Edit className="w-3.5 h-3.5" /> Edit
                                  </button>
                                  {report.pdf_url && (
                                    <button
                                      onClick={() => handleDownload(report)}
                                      disabled={resolvingReportId === report.id}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                    >
                                      <Icon.Download className="w-3.5 h-3.5" /> {resolvingReportId === report.id ? 'Preparing…' : 'Download'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

      {/* PDF Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-screen overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">{previewName}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <iframe src={previewUrl || ''} className="w-full h-96" title={previewName} />
            </div>
          </div>
        </div>
      )}

      {/* Edit overlay */}
      {isEditing && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg px-6 py-5 text-center">
            <div className="mx-auto mb-3 w-10 h-10 rounded-full border-4 border-[#F0D8D6] border-t-[#722420] animate-spin" />
            <p className="text-gray-900 font-semibold">Opening report…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;
